import TwoDimSlider from "./TwoDimSlider";
import {
  Box,
  Checkbox,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import { useTranslation } from "react-i18next";

function percentToStep(value) {
  return Math.round((Number(value || 0) / 100) * 4);
}

function calculateImpactRating(answer) {
  return (
    ((Number(answer.xAxis?.extent || 0) +
      Number(answer.xAxis?.scope || 0) +
      Number(answer.xAxis?.immutability || 0)) /
      3) *
    (Number(answer.xAxis?.probability || 0) / 100)
  );
}

function calculateFinancialRating(answer) {
  return (
    Number(answer.yAxis?.extent || 0) *
    (Number(answer.yAxis?.probability || 0) / 100)
  );
}

function Field({ label, value, onChange, disabled = false, rows = 2 }) {
  const { t } = useTranslation();

  return (
    <TextField
      color="secondary"
      InputLabelProps={{ shrink: true }}
      label={t(label)}
      variant="outlined"
      size="small"
      multiline
      rows={rows}
      disabled={disabled}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      sx={{ width: 1, mt: 1 }}
      inputProps={{ style: { fontSize: 13, lineHeight: 1.2 } }}
    />
  );
}

export default function DisplayStructuredEntryQuestion({
  questionData,
  setQuestion,
  index,
}) {
  const { t } = useTranslation();

  function updateAnswerInfo(field, value) {
    setQuestion((prev) => ({
      ...prev,
      answerInfo: {
        ...prev.answerInfo,
        [field]: value,
      },
    }));
  }

  function updateEntryAnswer(entryIndex, updater) {
    setQuestion((prev) => ({
      ...prev,
      requirementsWithAnswers: prev.requirementsWithAnswers.map((entry, i) =>
        i === entryIndex
          ? {
              ...entry,
              answer:
                typeof updater === "function"
                  ? updater(entry.answer || {})
                  : { ...entry.answer, ...updater },
            }
          : entry,
      ),
    }));
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: 1, mb: 5 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 17 }}>
        {`${t("question")} ${index + 1}`}
        {questionData.question.title ? `: ${questionData.question.title}` : ""}
      </Typography>
      <Typography sx={{ mt: 1, whiteSpace: "pre-line", fontFamily: "Inter" }}>
        {questionData.question.description}
      </Typography>
      {questionData.question.source && (
        <Typography sx={{ mt: 1, fontSize: 13, color: "#777" }}>
          {`${t("source")}: ${questionData.question.source}`}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexDirection: "row", gap: 2, mt: 3 }}>
        <Field
          label="comments"
          rows={3}
          value={questionData.answerInfo?.comment}
          onChange={(value) => updateAnswerInfo("comment", value)}
        />
        <Field
          label="contact"
          rows={3}
          value={questionData.answerInfo?.contactPerson}
          onChange={(value) => updateAnswerInfo("contactPerson", value)}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {questionData.requirementsWithAnswers.map(
        ({ requirement, answer = {} }, entryIndex) => {
          const impactRating = calculateImpactRating(answer);
          const financialRating = calculateFinancialRating(answer);

          return (
            <Box
              key={requirement.id}
              sx={{
                borderBottom: 1,
                borderColor: "#bbb",
                pb: 2,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                }}
              >
                <Box sx={{ width: 0.55 }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {`${t("impact")} ${entryIndex + 1}:`}
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontFamily: "Inter" }}>
                    {requirement.text}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography sx={{ fontSize: 12, mr: 0.5 }}>
                    {t("notRelevant")}
                  </Typography>
                  <Checkbox
                    size="small"
                    checked={answer.disabled || false}
                    icon={<NotInterestedIcon />}
                    checkedIcon={<NotInterestedIcon />}
                    onChange={() =>
                      updateEntryAnswer(entryIndex, (prev) => ({
                        ...prev,
                        disabled: !prev.disabled,
                      }))
                    }
                  />
                </Box>
              </Box>

              {!answer.disabled && (
                <>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        lg: "1fr 1fr 1fr",
                      },
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 500 }}>
                        Impact Materiality: {impactRating.toFixed(2)}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                        <TwoDimSlider
                          firstItem
                          value={Number(answer.xAxis?.extent || 0)}
                          title="Extent"
                          onChange={(event) =>
                            updateEntryAnswer(entryIndex, (prev) => ({
                              ...prev,
                              xAxis: {
                                ...prev.xAxis,
                                extent: Number(event.target.value),
                              },
                            }))
                          }
                        />
                        <TwoDimSlider
                          value={Number(answer.xAxis?.scope || 0)}
                          title="Scope"
                          onChange={(event) =>
                            updateEntryAnswer(entryIndex, (prev) => ({
                              ...prev,
                              xAxis: {
                                ...prev.xAxis,
                                scope: Number(event.target.value),
                              },
                            }))
                          }
                        />
                        <TwoDimSlider
                          value={Number(answer.xAxis?.immutability || 0)}
                          title="Immutability"
                          onChange={(event) =>
                            updateEntryAnswer(entryIndex, (prev) => ({
                              ...prev,
                              xAxis: {
                                ...prev.xAxis,
                                immutability: Number(event.target.value),
                              },
                            }))
                          }
                        />
                        <TwoDimSlider
                          value={percentToStep(answer.xAxis?.probability)}
                          title="Probability"
                          onChange={(event) =>
                            updateEntryAnswer(entryIndex, (prev) => ({
                              ...prev,
                              xAxis: {
                                ...prev.xAxis,
                                probability: Number(event.target.value) * 25,
                              },
                            }))
                          }
                        />
                      </Box>
                      <Field
                        label="comments"
                        value={answer.commentImpact}
                        onChange={(value) =>
                          updateEntryAnswer(entryIndex, {
                            commentImpact: value,
                          })
                        }
                      />
                    </Box>

                    <Box>
                      <Typography sx={{ fontWeight: 500 }}>
                        Financial Materiality: {financialRating.toFixed(2)}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                        <TwoDimSlider
                          firstItem
                          value={Number(answer.yAxis?.extent || 0)}
                          title="Extent"
                          onChange={(event) =>
                            updateEntryAnswer(entryIndex, (prev) => ({
                              ...prev,
                              yAxis: {
                                ...prev.yAxis,
                                extent: Number(event.target.value),
                              },
                            }))
                          }
                        />
                        <TwoDimSlider
                          value={percentToStep(answer.yAxis?.probability)}
                          title="Probability"
                          onChange={(event) =>
                            updateEntryAnswer(entryIndex, (prev) => ({
                              ...prev,
                              yAxis: {
                                ...prev.yAxis,
                                probability: Number(event.target.value) * 25,
                              },
                            }))
                          }
                        />
                      </Box>
                      <Field
                        label="comments"
                        value={answer.commentFinancial}
                        onChange={(value) =>
                          updateEntryAnswer(entryIndex, {
                            commentFinancial: value,
                          })
                        }
                      />
                    </Box>

                    <Box>
                      <Typography sx={{ fontWeight: 500 }}>
                        Stakeholder relevance: {Number(answer.relevance || 0)}
                      </Typography>
                      <TwoDimSlider
                        firstItem
                        value={Number(answer.relevance || 0)}
                        title="Rating"
                        onChange={(event) =>
                          updateEntryAnswer(entryIndex, {
                            relevance: Number(event.target.value),
                          })
                        }
                      />
                      <Field
                        label="comments"
                        value={answer.commentStakeholder}
                        onChange={(value) =>
                          updateEntryAnswer(entryIndex, {
                            commentStakeholder: value,
                          })
                        }
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        lg: "1fr 1fr 1fr",
                      },
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    <Field
                      label="risk"
                      value={answer.risk ?? requirement.risk}
                      onChange={(value) =>
                        updateEntryAnswer(entryIndex, { risk: value })
                      }
                    />
                    <Field
                      label="opportunity"
                      value={answer.opportunity ?? requirement.opportunity}
                      onChange={(value) =>
                        updateEntryAnswer(entryIndex, { opportunity: value })
                      }
                    />
                    <Field
                      label="example"
                      value={answer.example ?? requirement.example}
                      onChange={(value) =>
                        updateEntryAnswer(entryIndex, { example: value })
                      }
                    />
                  </Box>
                </>
              )}
            </Box>
          );
        },
      )}
    </Box>
  );
}
